import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, FormattedDate } from 'react-intl';
import moment from 'moment';
import cn from 'classnames';

import { bindAll } from '@bibliocommons/utils-react';
import { listShape, userIdentityShape } from '@bibliocommons/bc-prop-types';
import { truncate } from '@bibliocommons/utils-string';
import { Link } from '@bibliocommons/base-links';
import { Lattice } from '@bibliocommons/base-icons';
import Context from '@bibliocommons/context';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';

import './ListCard.scss';

const DESCRIPTION_MAX_LENGTH = 120;

export default class ListCard extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      useDefaultImage: false
    };
    bindAll(this);
  }

  useDefaultImage() {
    this.setState({ useDefaultImage: true });
  }

  getHeadingClass() {
    return cn('list-card__title', {
      'list-card__title--small': this.props.smallHeading
    });
  }

  renderDescription() {
    if (!this.props.list.get('description')) {
      return null;
    }

    const description = truncate(this.props.list.get('description', ''), {
      desiredLength: DESCRIPTION_MAX_LENGTH
    });

    return <div className="list-card__description">{description}</div>;
  }

  renderDate() {
    return (
      <div className="list-card__date">
        <FormattedDate
          value={moment.utc(this.props.list.get('created')).toDate()}
          year="numeric"
          month="short"
          day="2-digit"
        />
      </div>
    );
  }

  getImageUrl() {
    const { largeImage, list } = this.props;

    let imageUrl = list.get('imageUrl');
    if (!largeImage) {
      return imageUrl;
    }

    const imagePath = list.get('imagePath');
    const largeImagePath = list.get('largeImagePath');

    if (imageUrl && imagePath && largeImagePath) {
      imageUrl = imageUrl.replace(imagePath, largeImagePath);
    }

    return imageUrl;
  }

  handleListImage() {
    if (typeof this.props.handleListImageClick === 'function') {
      this.props.handleListImageClick();
    }
  }

  handleListTitle() {
    if (typeof this.props.handleListTitleClick === 'function') {
      this.props.handleListTitleClick();
    }
  }

  render() {
    const { creator, horizontal, list, renderUserCard, showDescription, showDate } = this.props;
    const listUrl = `/list/share/${creator.get('id')}/${list.get('id')}`;
    const imageUrl = this.getImageUrl();
    const TitleTag = this.props.titleTag;

    return (
      <div className={cn('cp-list-card', { 'list-card--horizontal': horizontal })}>
        <div className="list-card__image-wrapper">
          <Link
            href={listUrl}
            className="list-card__image-link"
            dataKey="list-card-image-link"
            aria-hidden="true"
            tabIndex="-1"
            handleClick={this.handleListImage}
          >
            {this.state.useDefaultImage ? (
              <Lattice />
            ) : (
              <img
                className="list-card__image"
                src={imageUrl}
                alt={`${list.get('name')} - ${creator.get('name')}`}
                aria-hidden={this.props.imageIsDecorative}
                onError={this.useDefaultImage}
                loading="lazy"
              />
            )}
          </Link>
        </div>

        <div className="list-card__content">
          <div className="list-card__type" aria-hidden>
            <FormattedMessage id={`user_list_purpose_${list.get('listType').toLowerCase()}`} />
          </div>
          <ScreenReaderMessage>
            <FormattedMessage
              id="sr_list_type"
              values={{
                listType: <FormattedMessage id={`user_list_purpose_${list.get('listType').toLowerCase()}`} />
              }}
            />
          </ScreenReaderMessage>
          <Link
            href={listUrl}
            className={this.getHeadingClass()}
            dataKey="list-card-title-link"
            handleClick={this.handleListTitle}
          >
            <TitleTag>{list.get('name')}</TitleTag>
          </Link>
          {showDescription && this.renderDescription()}
          <div className="list-card__creator">{renderUserCard()}</div>
          {showDate && this.renderDate()}
        </div>
      </div>
    );
  }
}

ListCard.defaultProps = {
  titleTag: 'h3'
};

ListCard.propTypes = {
  creator: userIdentityShape.isRequired,
  handleListImageClick: PropTypes.func,
  handleListTitleClick: PropTypes.func,
  horizontal: PropTypes.bool,
  imageIsDecorative: PropTypes.bool,
  largeImage: PropTypes.bool,
  list: listShape.isRequired,
  renderUserCard: PropTypes.func.isRequired,
  showDate: PropTypes.bool,
  showDescription: PropTypes.bool,
  smallHeading: PropTypes.bool,
  titleTag: PropTypes.string
};

ListCard.contextType = Context;
